import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { AsQ, ParagraphSM, TitleXS } from '@/atoms/Text'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { HubpageSingleItemSlider } from '@/molecules/HubpageSingleItemSlider'
import { MovieRating } from '@/molecules/MovieRating'
import { MovieRatingsContainer } from '@/organisms/MovieRatingsContainer'
import { CriticScore, TheatricalReleaseObject } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { useWatchTheatricalContext } from '../WatchTheatricalContext'

interface ReviewsProps {
  theatricalRelease?: TheatricalReleaseObject | null
}

export const Reviews: React.FC<ReviewsProps> = ({ theatricalRelease }) => {
  const { t } = useTranslate('watch')
  const { rottenTomatoes, audience, cinema, critics, IMDb } = useWatchTheatricalContext()
  const projectMeta = useProjectMeta()

  const theatricalReleaseTomatometer = theatricalRelease?.critics?.[0]?.scores?.find(
    (score) => score?.name?.toLowerCase() === 'tomatometer',
  )
  const theatricalReleaseAudience = theatricalRelease?.critics?.[0]?.scores?.find(
    (score) => score?.name?.toLowerCase() === 'audience',
  )
  const certifiedFresh = Boolean(theatricalRelease?.critics?.[0]?.certifiedFresh)
  const rottenTomatoesRating =
    rottenTomatoes?.items[0] ||
    (theatricalReleaseTomatometer && mapTheatricalReleaseCriticToRating(theatricalReleaseTomatometer, certifiedFresh))
  const audienceRating =
    audience?.items[0] ||
    (theatricalReleaseAudience && mapTheatricalReleaseCriticToRating(theatricalReleaseAudience, certifiedFresh))
  const cinemaRating = cinema?.items[0]
  const IMDbRating = IMDb?.items?.[0]
  const { projectType } = projectMeta

  return (
    <div
      className={classNames('grid bg-black text-white', {
        'md:grid-cols-[250px_1fr]': critics?.items && critics.items.length > 0,
      })}
    >
      {critics?.items && critics.items.length > 0 && (
        <HubpageSingleItemSlider
          title={
            projectType === 'movie'
              ? t('movieReviews', 'Movie Reviews')
              : projectType === 'series'
              ? t('seriesReviews', 'Series Reviews')
              : t('reviews', 'Reviews')
          }
          className="flex w-full flex-col items-center justify-center bg-black text-white"
          sliderStyles="overflow-hidden w-full mx-auto"
          id="review-section-title"
        >
          {critics?.items?.map((review, index: number) => (
            <div
              key={index}
              className="my-2 !flex min-h-[120px] max-w-full place-content-center items-center rounded-lg !bg-core-gray-950 text-center md:min-h-[300px]"
            >
              <article className="px-8 py-4">
                <TitleXS
                  as={AsQ}
                  className="md:photon-title-sm lg:photon-heading-xs my-2 text-pretty"
                  weight="bold"
                >{`${review?.quote}`}</TitleXS>
                <ParagraphSM>
                  {review?.author}
                  {review?.company && `- ${review.company}`}
                </ParagraphSM>
              </article>
            </div>
          ))}
        </HubpageSingleItemSlider>
      )}
      <MovieRatingsContainer className="max-w-[200px]">
        <When
          condition={
            !!rottenTomatoesRating && !!rottenTomatoesRating.rating && parseInt(rottenTomatoesRating.rating) >= 75
          }
        >
          <MovieRating
            icon={rottenTomatoesRating?.icon?.[0]}
            rating={rottenTomatoesRating?.rating}
            author={rottenTomatoesRating?.author}
          />
        </When>
        <When condition={!!audienceRating && !!audienceRating.rating && parseInt(audienceRating.rating) >= 75}>
          <MovieRating
            icon={audienceRating?.icon?.[0]}
            rating={audienceRating?.rating}
            author={audienceRating?.author}
          />
        </When>
        <When condition={!!cinemaRating}>
          <MovieRating icon={cinemaRating?.icon?.[0]} rating={cinemaRating?.rating} author={cinemaRating?.author} />
        </When>
        <When condition={!!IMDbRating}>
          <MovieRating icon={IMDbRating?.icon?.[0]} rating={IMDbRating?.rating} author={IMDbRating?.author} />
        </When>
      </MovieRatingsContainer>
    </div>
  )
}

export const mapTheatricalReleaseCriticToRating = (
  score: CriticScore,
  certifiedFresh?: boolean,
): { icon: { url: string; public_id: string; version: string }[]; rating: string; author: string } | null => {
  if (!score?.value) {
    return null
  }

  return {
    icon: [getRatingIcon(score?.name ?? undefined, certifiedFresh)],
    rating: `${score.value}%`,
    author: score?.name ?? 'Audience',
  }
}

const getRatingIcon = (scoreName?: string, certifiedFresh?: boolean) => {
  if (!scoreName) {
    return {
      public_id: 'angel-studios/misc-icons/Audience-Score',
      version: '1724953395',
      url: 'http://images.angelstudios.com/image/upload/f_auto/q_auto/v1724953395/angel-studios/misc-icons/Audience-Score',
    }
  }

  const lowerScoreName = scoreName.toLowerCase()

  if (lowerScoreName === 'tomatometer' && certifiedFresh) {
    return {
      public_id: 'angel-studios/misc-icons/Certified-Fresh',
      version: '1724953395',
      url: 'https://images.angelstudios.com/image/upload/f_auto/q_auto/v1724953395/angel-studios/misc-icons/Certified-Fresh',
    }
  }

  if (lowerScoreName === 'audience') {
    return {
      public_id: 'angel-studios/misc-icons/Rotten_Tomatoes_positive_audience',
      version: '1760723104',
      url: 'https://images.angelstudios.com/image/upload/v1760723104/angel-studios/misc-icons/Rotten_Tomatoes_positive_audience',
    }
  }

  return {
    public_id: 'angel-studios/misc-icons/rotten-tomatoe',
    version: '1688691818',
    url: 'https://images.angelstudios.com/image/upload/v1688691818/angel-studios/misc-icons/rotten-tomatoe',
  }
}
